import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Loader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { useState } from "react";
import type { PaymentWithMethod, RefundInput } from "../../domain/types";
import { useCreateRefund, useOrderPayments } from "../api/queries";

type RefundDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  branchId: string | null;
  orderTotal: number;
  paidTotal: number;
};

export function RefundDialog({
  open,
  onOpenChange,
  orderId,
  branchId,
  orderTotal,
  paidTotal,
}: RefundDialogProps) {
  const { data: payments, isLoading: isLoadingPayments } = useOrderPayments(
    orderId,
    branchId,
  );
  const createRefundMutation = useCreateRefund();

  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<string>>(
    new Set(),
  );
  const [reasonCode, setReasonCode] = useState<
    "CUSTOMER_REQUEST" | "ITEM_ISSUE" | "PAYMENT_ERROR" | "OTHER"
  >("OTHER");
  const [reason, setReason] = useState("");
  const [refundAll, setRefundAll] = useState(false);

  const availablePayments = payments ?? [];
  const selectedPayments = availablePayments.filter((p) =>
    selectedPaymentIds.has(p.id),
  );
  const selectedTotal = selectedPayments.reduce(
    (sum, p) => sum + Number(p.amountGross),
    0,
  );

  const handleTogglePayment = (paymentId: string) => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      if (next.has(paymentId)) {
        next.delete(paymentId);
      } else {
        next.add(paymentId);
      }
      return next;
    });
  };

  const handleToggleRefundAll = (checked: boolean) => {
    setRefundAll(checked);
    if (checked) {
      setSelectedPaymentIds(new Set(availablePayments.map((p) => p.id)));
    } else {
      setSelectedPaymentIds(new Set());
    }
  };

  const handleSubmit = async () => {
    if (!branchId) {
      toast.error("Branch ID is required");
      return;
    }

    if (!refundAll && selectedPaymentIds.size === 0) {
      toast.error("ກະລຸນາເລືອກ payment ທີ່ຕ້ອງການຄືນເງິນ");
      return;
    }

    try {
      const refundInput: RefundInput = {
        paymentIds: refundAll ? [] : Array.from(selectedPaymentIds),
        reasonCode,
        reason: reason.trim() || undefined,
      };

      await createRefundMutation.mutateAsync({
        orderId,
        branchId,
        refundInput,
      });

      toast.success("ສ້າງ refund request ສຳເລັດແລ້ວ");
      onOpenChange(false);
      // Reset form
      setSelectedPaymentIds(new Set());
      setReasonCode("OTHER");
      setReason("");
      setRefundAll(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການສ້າງ refund",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ຄືນເງິນ</DialogTitle>
          <DialogDescription>
            Order Total: {orderTotal.toLocaleString("lo-LA")} ₭ | Paid:{" "}
            {paidTotal.toLocaleString("lo-LA")} ₭
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoadingPayments ? (
            <div className="flex items-center justify-center py-8">
              <Loader />
            </div>
          ) : (
            <>
              {/* Refund All Option */}
              <div className="flex items-center space-x-2 rounded-md border p-3">
                <Checkbox
                  id="refund-all"
                  checked={refundAll}
                  onCheckedChange={handleToggleRefundAll}
                />
                <label
                  htmlFor="refund-all"
                  className="cursor-pointer font-medium text-sm"
                >
                  ຄືນເງິນທັງໝົດ ({paidTotal.toLocaleString("lo-LA")} ₭)
                </label>
              </div>

              {/* Payment List */}
              {!refundAll && (
                <div className="space-y-2 rounded-md border p-3">
                  <h4 className="font-medium text-sm">
                    ເລືອກ Payment ທີ່ຕ້ອງການຄືນເງິນ:
                  </h4>
                  {availablePayments.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີ payment ທີ່ສາມາດຄືນເງິນໄດ້
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availablePayments.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex items-center space-x-2 rounded-md border p-2"
                        >
                          <Checkbox
                            id={`payment-${payment.id}`}
                            checked={selectedPaymentIds.has(payment.id)}
                            onCheckedChange={() =>
                              handleTogglePayment(payment.id)
                            }
                          />
                          <label
                            htmlFor={`payment-${payment.id}`}
                            className="flex-1 cursor-pointer text-sm"
                          >
                            <div className="flex items-center justify-between">
                              <span>{payment.method.displayName}</span>
                              <span className="font-medium">
                                {Number(payment.amountGross).toLocaleString(
                                  "lo-LA",
                                )}{" "}
                                ₭
                              </span>
                            </div>
                            {payment.referenceCode && (
                              <p className="text-muted-foreground text-xs">
                                Ref: {payment.referenceCode}
                              </p>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Total */}
              {!refundAll && selectedPaymentIds.size > 0 && (
                <div className="rounded-md bg-muted p-3">
                  <p className="font-medium text-sm">
                    ຈຳນວນທີ່ຈະຄືນເງິນ: {selectedTotal.toLocaleString("lo-LA")} ₭
                  </p>
                </div>
              )}

              {/* Reason Code */}
              <div>
                <label className="mb-2 block font-medium text-sm">ເຫດຜົນ:</label>
                <Select
                  value={reasonCode}
                  onValueChange={(
                    value:
                      | "CUSTOMER_REQUEST"
                      | "ITEM_ISSUE"
                      | "PAYMENT_ERROR"
                      | "OTHER",
                  ) => setReasonCode(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CUSTOMER_REQUEST">ລູກຄ້າຂໍຄືນເງິນ</SelectItem>
                    <SelectItem value="ITEM_ISSUE">ບັນຫາສິນຄ້າ</SelectItem>
                    <SelectItem value="PAYMENT_ERROR">ຂໍ້ຜິດພາດການຊຳລະ</SelectItem>
                    <SelectItem value="OTHER">ອື່ນໆ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reason Notes */}
              <div>
                <label className="mb-2 block font-medium text-sm">
                  ໝາຍເຫດ (ທາງເລືອກ):
                </label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="ປ້ອນໝາຍເຫດ..."
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createRefundMutation.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createRefundMutation.isPending ||
              (!refundAll && selectedPaymentIds.size === 0)
            }
          >
            {createRefundMutation.isPending
              ? "ກຳລັງສ້າງ..."
              : "ສ້າງ Refund Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
